
import { Component, computed, effect, ElementRef, HostListener, inject, signal } from '@angular/core';
import { isAppUserReady, userCenterSignal, userFullNameSignal, userIdSignal, userPermissionsSignal, userRoleSignal } from '../../state/auth.state';
import { AuthService } from '../../components/login/auth.service';
import { Router, RouterModule } from '@angular/router';
import { isMapView } from '../../state/dashboard.state';
import { CommonModule } from '@angular/common';
import { NotificacionesService } from './notificaciones.service';
import { ComentariosNoLeidosResponse } from '../../compartidos/DTOs/ComentariosNoLeidosResponse';
import { firstValueFrom } from 'rxjs';
import { NotificacionesAlarmasResponse } from '../../compartidos/DTOs/NotificacionesAlarmasResponse';
import { TiempoRelativoPipe } from '../../compartidos/pipes/tiempo-relativo.pipe';
import {
  clienteActivoIdSignal,
  clienteSeleccionadoSignal,
  clientesDisponiblesSignal,
  seleccionarCliente,
  tieneMultiplesClientesSignal,
  universosSignal,
  universoActivoSignal,
  tieneMultiplesUniversosSignal
} from '../../compartidos/services/client-context.state';
import { currentUserSignal } from '../../state/auth.state';
import { AppStateService } from '../../compartidos/services/app-state.service';
declare const bootstrap: any;

/**
 * Etiqueta de catálogo (Cat_ParametrosLectura.cEtiquetaResumen) que marca las
 * mediciones de calidad. Misma convención que usa el mapa para decidir a dónde
 * navegar: no es una clase de medidor hardcodeada.
 */
const ETIQUETA_CALIDAD = 'Calidad';

@Component({
  selector: 'app-header',
  imports: [RouterModule, CommonModule, TiempoRelativoPipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {

  // ─────────────────────────────────────────────
  // Injects
  // ─────────────────────────────────────────────
  private srvAuth = inject(AuthService);
  private srvRouter = inject(Router);
  private srvNotificaciones = inject(NotificacionesService);
  private appState = inject(AppStateService);
  private el = inject(ElementRef);

  // ─────────────────────────────────────────────
  // Signals globales
  // ─────────────────────────────────────────────
  userFullName = userFullNameSignal;
  userRole = userRoleSignal;
  centerUser = userCenterSignal;
  isAppUserReady = isAppUserReady;
  isMapView = isMapView;
  userPermissionsSignal = userPermissionsSignal;

  // ─────────────────────────────────────────────
  // Contexto de cliente
  // ─────────────────────────────────────────────
  clientesDisponibles = clientesDisponiblesSignal;
  clienteSeleccionado = clienteSeleccionadoSignal;
  tieneMultiplesClientes = tieneMultiplesClientesSignal;

  // PASO 16: el selector cambia de UNIVERSO, no de cliente.
  universos = universosSignal;
  universoActivo = universoActivoSignal;
  tieneMultiplesUniversos = tieneMultiplesUniversosSignal;

  // ─────────────────────────────────────────────
  // Estado local
  // ─────────────────────────────────────────────
  notificacionesNoLeidas = signal<number>(0);
  dropdownVisible = signal(false);
  comentariosNoVistos = signal<ComentariosNoLeidosResponse['comentarios']>([]);
  notificacionesAlarmas = signal<NotificacionesAlarmasResponse['notificaciones']>([]);

  // ─────────────────────────────────────────────
  // Computeds seguros
  // ─────────────────────────────────────────────
  /**
   * El menú de administración se muestra si el usuario tiene AL MENOS una de
   * sus capacidades. Cada entrada de adentro se filtra por su propio permiso:
   * administrar accesos y dar de alta clientes son cosas distintas.
   */
  puedeAdministrar = computed(() => {
    const permisos = userPermissionsSignal();

    return permisos.includes('administrar_usuarios')
        || permisos.includes('administrar_clientes');
  });

  comentariosVacios = computed(() => this.comentariosNoVistos().length === 0);
  totalNotificaciones = computed(() => this.notificacionesNoLeidas() + this.notificacionesAlarmas().length);
  notificacionesAlarmasSafe = computed(() => this.notificacionesAlarmas() ?? []);
  comentariosNoVistosSafe = computed(() => this.comentariosNoVistos() ?? []);

  // ─────────────────────────────────────────────
  // Módulos de análisis disponibles por cliente
  //
  // Un cliente puede medir varias cosas a la vez, así que NO son excluyentes:
  // cada módulo se evalúa por su cuenta contra lo que el cliente realmente
  // tiene medido. Se derivan de las apps destacadas, que ya están en memoria;
  // no hace falta ninguna consulta extra ni una bandera por cliente.
  //
  // Mientras no haya apps cargadas se ocultan: es preferible que el menú
  // crezca al resolverse el contexto a ofrecer una pantalla vacía.
  // ─────────────────────────────────────────────

  /** Hay conductividad: aplica el control de sólidos disueltos. */
  readonly tieneCalidad = computed(() =>
    this.appState.featuredApps()
      .some(a => a.etiquetaResumen === ETIQUETA_CALIDAD)
  );

  /**
   * Hay medición agrupada por sitio: es lo que el balance compara.
   *
   * PASO 13b: antes preguntaba por `siteGroupId`. Un cliente nuevo sin grupo
   * quedaba sin la opción en el menú aunque tuviera medidores volumétricos.
   */
  readonly tieneBalance = computed(() =>
    this.appState.featuredApps()
      .some(a => a.etiquetaResumen !== ETIQUETA_CALIDAD && !!a.siteId)
  );

  /**
   * Iniciales para el avatar del menú de usuario.
   *
   * Consolidar nombre + rol + salir en un solo control libera el espacio que
   * la barra necesita para mostrar los módulos directo, sin esconderlos.
   */
  readonly iniciales = computed(() => {
    const nombre = (this.userFullName() ?? '').trim();
    if (!nombre) return '?';

    const partes = nombre.split(/\s+/).filter(Boolean);

    return partes.length === 1
      ? partes[0].charAt(0).toUpperCase()
      : (partes[0].charAt(0) + partes[1].charAt(0)).toUpperCase();
  });


  // ─────────────────────────────────────────────
  // Click fuera del dropdown
  // ─────────────────────────────────────────────
  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    if (this.dropdownVisible() && this.el.nativeElement && !this.el.nativeElement.contains(event.target)) {
      this.dropdownVisible.set(false);
    }
  }

  // ─────────────────────────────────────────────
  // Init reactivo
  // ─────────────────────────────────────────────
  constructor() {
    // Las notificaciones dependen del usuario Y del cliente seleccionado:
    // al cambiar de cliente deben recargarse.
    effect(() => {
      const userId = userIdSignal();
      const clienteId = clienteActivoIdSignal();

      if (!userId || userId <= 0) return;
      if (!clienteId) return;

      this.actualizarNotificaciones(userId, clienteId);
    });
  }

  // ─────────────────────────────────────────────
  // Cambio de cliente
  // ─────────────────────────────────────────────
  cambiarCliente(iIdCliente: number) {
    const uid = currentUserSignal()?.uid ?? null;

    // seleccionarCliente valida contra la lista permitida y devuelve false
    // si no hubo cambio real. Al cambiar, userCenterSignal se actualiza y
    // los módulos que dependen del center recargan solos.
    seleccionarCliente(iIdCliente, uid);
    this.closeNavbar();
  }

  // ─────────────────────────────────────────────
  // Sesión
  // ─────────────────────────────────────────────
  async cerrarSesion() {
    await this.srvAuth.logout();
    this.srvRouter.navigate(['/login']);
  }

  // ─────────────────────────────────────────────
  // Navbar móvil
  // ─────────────────────────────────────────────
  closeNavbar() {
    const navbar = document.getElementById('navbarMain');
    if (!navbar) return;

    if (window.innerWidth < 768) {
      try {
        bootstrap.Collapse.getOrCreateInstance(navbar).hide();
      } catch {
        navbar.classList.remove('show');
      }
    }
  }

  // ─────────────────────────────────────────────
  toggleDropdown() {
    this.dropdownVisible.set(!this.dropdownVisible());
  }

  // ─────────────────────────────────────────────
  // Comentario → detalle alarma
  // ─────────────────────────────────────────────
  async irADetalleAlarma(comentario: ComentariosNoLeidosResponse['comentarios'][number]) {
    try {
      const userId = userIdSignal();
      const clienteId = clienteActivoIdSignal();
      if (!userId) return;

      await firstValueFrom(
        this.srvNotificaciones.marcarComentariosVistos([comentario.iIdComentario], userId, clienteId)
      );
      await this.actualizarNotificaciones(userId, clienteId);

      this.dropdownVisible.set(false);
      this.srvRouter.navigateByUrl(`/alarma-detalle/${comentario.meterId}`);
    } catch (err) {
      console.error('Error al abrir comentario', err);
    }
  }

  // ─────────────────────────────────────────────
  // Notificación alarma → detalle
  // ─────────────────────────────────────────────
  async irANotificacionAlarma(n: NotificacionesAlarmasResponse['notificaciones'][number]) {
    try {
      const userId = userIdSignal();
      const clienteId = clienteActivoIdSignal();
      if (!userId) return;

      await firstValueFrom(this.srvNotificaciones.marcarNotificacionVista(n.iId, userId, clienteId));

      this.notificacionesAlarmas.update(list =>
        list.filter(x => x.iId !== n.iId)
      );

      this.dropdownVisible.set(false);
      this.srvRouter.navigateByUrl(
        `/alarma-detalle/${n.iIdMedidor}`
      );
    } catch (err) {
      console.error('Error al abrir alarma', err);
    }
  }

  // ─────────────────────────────────────────────
  // Carga central de notificaciones
  // ─────────────────────────────────────────────
  private async actualizarNotificaciones(userId: number, clienteId: number | null) {
    try {
      // Comentarios
      const resComentarios = await firstValueFrom(
        this.srvNotificaciones.getComentariosNoLeidos(userId, clienteId)
      );

      this.notificacionesNoLeidas.set(resComentarios.totalNoVistos);
      this.comentariosNoVistos.set(resComentarios.comentarios);

      // Alarmas
      const resAlarmas = await firstValueFrom(
        this.srvNotificaciones.getNotificacionesAlarmas(userId, clienteId)
      );

      this.notificacionesAlarmas.set(resAlarmas.notificaciones ?? []);

    } catch {
      this.notificacionesNoLeidas.set(0);
      this.comentariosNoVistos.set([]);
      this.notificacionesAlarmas.set([]);
    }
  }

  // ─────────────────────────────────────────────
  // Vista mapa / tarjetas
  // ─────────────────────────────────────────────
  switchView(isMapa: boolean) {
    this.isMapView.set(isMapa);
    this.closeNavbar();
  }
}
